import { convertUserToContact } from '@debbie/common/dist/cases/index.js';
import { getContactPerson } from '@debbie/common/dist/customers/index.js';
import { getFoundations, isDocumentFoundation, } from '@debbie/common/dist/documents/index.js';
import { Entity, PermissionLevels } from '@debbie/common/dist/index.js';
import { isCaseworker } from '@debbie/common/dist/permissions/index.js';
import { UpdateType, } from '@debbie/common/dist/updates/index.js';
import { uniqueBy } from '@debbie/common/dist/util/index.js';
import { RequirementType, getNextRequirements, getNextTask, usesDebtRegister, } from '@debbie/common/dist/workflows/index.js';
import * as propertiesAPI from '@debbie/cortex/dist/api/properties.js';
import { useCaseVouchersByCase } from '@debbie/cortex/dist/case-vouchers/index.js';
import { useCustomer } from '@debbie/cortex/dist/customers/index.js';
import { useRegistriesByCaseId } from '@debbie/cortex/dist/debt-registries/index.js';
import { useDocumentsByReference } from '@debbie/cortex/dist/documents/index.js';
import { h } from '@debbie/cortex/dist/index.js';
import { useInteractionsByReference } from '@debbie/cortex/dist/interactions/index.js';
import { useRole } from '@debbie/cortex/dist/me/index.js';
import { useProperties } from '@debbie/cortex/dist/properties/index.js';
import { useUpdateTypesMap } from '@debbie/cortex/dist/update-types/index.js';
import { useUpdatesByRefId } from '@debbie/cortex/dist/updates/index.js';
import { useWorkflows } from '@debbie/cortex/dist/workflows/index.js';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { APIBASE } from '../../config.js';
import { Button, Modal } from '..//index.js';
import NewPause from '../NewPause.js';
import UpdateTypeLabel from '../UpdateTypeLabel.js';
import ActiveUpdate from './ActiveUpdate.js';
import CasePauseActions from './CasePauseActions.js';
import TaskButtons from './TaskButtons.js';
export const getActivePauses = (updates, updateTypesMap, meRole) => {
    if (!updates || !meRole || !updateTypesMap) {
        return [];
    }
    const activePauses = [];
    updates.forEach((update) => {
        if (update.continuedAt && !moment(update.continuedAt).isAfter(moment())) {
            return;
        }
        const updateType = updateTypesMap[update.updateTypeId];
        if (!updateType) {
            return;
        }
        const hasPermission = !!updateType.responsiblePermissionLevel &&
            (updateType.responsiblePermissionLevel === meRole ||
                (updateType.responsiblePermissionLevel ===
                    PermissionLevels.caseworker &&
                    meRole === PermissionLevels.admin));
        const canContinueCase = hasPermission && isCaseworker(meRole);
        const isHidden = !!updateType.hiddenPermissionLevel &&
            updateType.hiddenPermissionLevel === meRole;
        if (!isHidden) {
            activePauses.push({
                update,
                updateType,
                canContinueCase,
                isAwaitingClient: updateType.updateTypeId === UpdateType.AWAITINGCLIENTANSWER,
                futureReminder: moment(update.remindedAt).isBefore(moment())
                    ? null
                    : moment(update.remindedAt),
                futurePause: moment(update.pausedAt).isBefore(moment())
                    ? null
                    : moment(update.pausedAt),
            });
        }
    });
    return activePauses.sort((a, b) => moment(b.update.createdAt).toDate().getTime() -
        moment(a.update.createdAt).toDate().getTime());
};
const CasePauseStatus = (props) => {
    const updateTypesMapLoadable = useUpdateTypesMap();
    const updateTypesMap = h.hasData(updateTypesMapLoadable)
        ? updateTypesMapLoadable.data
        : {};
    const customerLoader = useCustomer(props.case.customerId);
    const customer = h.hasData(customerLoader) ? customerLoader.data : null;
    const meRole = useRole();
    const updatesLoadable = useUpdatesByRefId(props.case.caseId);
    const caseUpdates = h.hasData(updatesLoadable) ? updatesLoadable.data : null;
    const caseVouchers_ = useCaseVouchersByCase(props.case.caseId);
    const caseVouchers = h.hasData(caseVouchers_) ? caseVouchers_.data : null;
    const workflowsLoadable = useWorkflows();
    const workflows = h.hasData(workflowsLoadable) ? workflowsLoadable.data : null;
    const propertiesLoadable = useProperties(Entity.Case, props.case?.caseId);
    const properties = h.hasData(propertiesLoadable)
        ? propertiesLoadable.data
        : null;
    const customerPropertiesLoadable = useProperties(Entity.Customer, props.case.customerId);
    const customerProperties = h.hasData(customerPropertiesLoadable)
        ? customerPropertiesLoadable.data
        : null;
    const creditorPropertiesLoadable = useProperties(Entity.Creditor, props.case.creditorId);
    const creditorProperties = h.hasData(creditorPropertiesLoadable)
        ? creditorPropertiesLoadable.data
        : null;
    const documentsLoadable = useDocumentsByReference(props.case.caseId);
    const documents = h.hasData(documentsLoadable) ? documentsLoadable.data : null;
    const interactionsLoadable = useInteractionsByReference(Entity.Case, props.case.caseId);
    const interactions = h.hasData(interactionsLoadable)
        ? interactionsLoadable.data
        : null;
    const workflow = workflows?.find((workflow) => workflow.workflowId === props.case.workflowId);
    const needsDebtRegistrations = workflow !== undefined && usesDebtRegister(workflow);
    const debtRegistrationsLoadable = useRegistriesByCaseId(needsDebtRegistrations ? props.case.caseId : undefined);
    const loadedDebtRegistrations = h.hasData(debtRegistrationsLoadable)
        ? debtRegistrationsLoadable.data
        : null;
    const debtRegistrations = needsDebtRegistrations
        ? loadedDebtRegistrations
        : [];
    const hasFoundation = documents && !!documents.find((document) => isDocumentFoundation(document));
    const foundationTypes = documents
        ? uniqueBy(getFoundations(documents), (d) => d.type).map((d) => d.type)
        : [];
    const [deletedUpdates, setDeletedUpdates] = useState(new Set());
    const [caseVoucherProperties, setCaseVoucherProperties] = useState(null);
    const activePauseUpdates = getActivePauses(caseUpdates || [], updateTypesMap, meRole);
    const displayPauses = [];
    activePauseUpdates.forEach((pause) => {
        const { update } = pause;
        if (deletedUpdates.has(update.updateId)) {
            return;
        }
        if (update.update) {
            displayPauses.push({
                shouldBeStacked: false,
                pauses: [pause],
                updateTypeId: update.updateTypeId,
            });
            return;
        }
        const existingPauseStack = displayPauses.find((p) => p.updateTypeId === update.updateTypeId && p.shouldBeStacked);
        if (existingPauseStack) {
            displayPauses[displayPauses.indexOf(existingPauseStack)].pauses.push(pause);
            return;
        }
        displayPauses.push({
            shouldBeStacked: true,
            pauses: [pause],
            updateTypeId: update.updateTypeId,
        });
    });
    const toggleShowNewUpdate = () => props.setShowNewUpdate && props.setShowNewUpdate(!props.showNewUpdate);
    const loadCaseVoucherProperties = async (_caseVouchers) => {
        const caseVoucherProperties = await propertiesAPI.getMultiple({
            ref: Entity.CaseVoucher,
            refIds: _caseVouchers.map((cv) => cv.caseVoucherId),
        });
        setCaseVoucherProperties(Object.values(caseVoucherProperties).reduce((a, b) => a.concat(b), []));
    };
    useEffect(() => {
        // TODO: Use cortex queries for this
        if (workflow && caseUpdates && caseVouchers) {
            if (getNextRequirements(workflow, caseUpdates)
                .map((r) => r.type)
                .includes(RequirementType.Property) &&
                caseVouchers.length > 0) {
                // Load case voucher properties
                loadCaseVoucherProperties(caseVouchers);
            }
            else {
                setCaseVoucherProperties([]);
            }
        }
    }, [workflow, caseUpdates, caseVouchers]);
    const nextTask = workflow &&
        caseUpdates &&
        props.case &&
        caseVouchers &&
        customer &&
        properties &&
        customerProperties !== null &&
        creditorProperties !== null &&
        interactions &&
        debtRegistrations &&
        Array.isArray(caseVoucherProperties) &&
        typeof hasFoundation === 'boolean'
        ? getNextTask(workflow, {
            contact: convertUserToContact(getContactPerson(customer, props.case)),
            isCompany: props.case.isCompany,
            caseStatus: props.case.status,
            updates: caseUpdates,
            caseVouchers: caseVouchers,
            caseGroupId: props.case.caseGroup?.id || null,
            caseId: props.case.caseId,
            endReasonId: props.case.endReasonId,
            creditorId: props.case.creditorId,
            hasFoundation,
            foundationTypes,
            customerTags: customer.tags,
            caseTags: props.case.tags,
            interactions: interactions,
            properties: properties.concat(caseVoucherProperties, customerProperties, creditorProperties),
            debtRegistrations,
        })
        : undefined;
    return (React.createElement("div", null,
        props.showNewUpdate && props.case && (React.createElement(NewPause, { toggleNewUpdate: () => toggleShowNewUpdate(), case: props.case })),
        nextTask &&
            //@ts-ignore
            nextTask.manual &&
            //@ts-ignore
            nextTask.tasks &&
            !nextTask.pastUpdate &&
            //@ts-ignore
            !!nextTask.tasks.length && (React.createElement(ActiveUpdate
        //@ts-ignore
        , { 
            //@ts-ignore
            actions: React.createElement(TaskButtons, { case: props.case, tasks: nextTask.tasks }), label: 'Vælg næste opgave' })),
        displayPauses.map((displayPause) => {
            return (React.createElement(ShowPause, { key: displayPause.pauses[0].update.updateId, onDeleteUpdate: (id, deleting) => {
                    setDeletedUpdates((deletedUpdates) => {
                        if (deleting) {
                            deletedUpdates.add(id);
                        }
                        else {
                            deletedUpdates.delete(id);
                        }
                        return new Set(deletedUpdates);
                    });
                }, displayPause: displayPause, singleCase: props.case, nextTask: nextTask }));
        })));
};
export default CasePauseStatus;
const ShowPause = (props) => {
    const { futureReminder, update, futurePause, updateType, canContinueCase, isAwaitingClient, } = props.displayPause.pauses[0];
    const { shouldBeStacked, pauses } = props.displayPause;
    const { nextTask } = props;
    const [deletingUpdate, setDeletingUpdate] = useState(false);
    useEffect(() => {
        setDeletingUpdate(false);
    }, [props.displayPause]);
    if (deletingUpdate) {
        return null;
    }
    return (React.createElement(React.Fragment, null,
        updateType && React.createElement(IntroduceUpdateType, { updateType: updateType }),
        React.createElement(ActiveUpdate, { actions: React.createElement(CasePauseActions, { singleCase: props.singleCase, nextTask: nextTask, futurePause: futurePause, update: update, updateType: updateType, canContinueCase: canContinueCase, isAwaitingClient: isAwaitingClient, stack: pauses, setDeletingUpdate: (update, deleting) => {
                    props.onDeleteUpdate(update, deleting);
                } }), futurePause: futurePause, futureReminder: futureReminder, quantity: shouldBeStacked ? pauses.length : 1, updateTypeId: updateType.updateTypeId, update: update })));
};
const introducedKey = (updateType) => `update-type-introduced-${updateType.updateTypeId}`;
const IntroduceUpdateType = (props) => {
    const { updateType } = props;
    const [introduced, _setIntroduced] = useState(!!localStorage.getItem(introducedKey(updateType)));
    const setIntroduced = (s) => {
        localStorage.setItem(introducedKey(updateType), s ? 'true' : '');
        _setIntroduced(s);
    };
    if (introduced || !updateType.introductionHTML) {
        return null;
    }
    return (React.createElement(Modal, { header: React.createElement(UpdateTypeLabel, { updateTypeId: updateType.updateTypeId, label: updateType.label }), onToggle: () => setIntroduced(true), maxWidth: "600px" },
        updateType.introductionHeroFile?.fileId && (React.createElement("img", { src: `${APIBASE}/files/${updateType.introductionHeroFile.fileId}/${updateType.introductionHeroFile.fileName}` })),
        React.createElement("div", { dangerouslySetInnerHTML: {
                __html: updateType.introductionHTML,
            } }),
        React.createElement("br", null),
        React.createElement("br", null),
        React.createElement(Button, { block: true, onClick: () => setIntroduced(true) }, "Ok")));
};
//# sourceMappingURL=CasePauseStatus.js.map